import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Checkbox, Input, Spin } from 'antd';
import { CheckCircleFilled, MailOutlined, SafetyCertificateOutlined, BellOutlined } from '@ant-design/icons';
import styles from './DemoOnboardingModal.module.scss';
import * as userApi from '../../api/index';
import { setAccessToken } from '../../api/config';
import { demoLoginUser } from '../../reduxStore/actions/userActionsHelper';


const STEP_META = [
    { icon: <MailOutlined />, label: 'Verify Email' },
    { icon: <SafetyCertificateOutlined />, label: 'Enter Code' },
    { icon: <CheckCircleFilled />, label: 'Terms' },
    { icon: <BellOutlined />, label: 'Updates' },
];

const RESEND_COOLDOWN = 60; // seconds

export default function DemoOnboardingModal() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [step, setStep] = useState(0);
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [otp, setOtp] = useState('');
    const [otpError, setOtpError] = useState('');
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);

    // Tokens returned from demoVerifyOTP — held locally until the final step
    const pendingAuth = useRef({ user: null, accessToken: null });

    // — Resend countdown timer  
    useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendCooldown]);

    // ——————————————————————————————————————
    // Step 0: send OTP
    // ——————————————————————————————————————
    const validateEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

    const handleSendOTP = async () => {
        if (!validateEmail(email)) {
            setEmailError('Please enter a valid email address.');
            return;
        }
        setEmailError('');
        setLoading(true);
        try {
            await userApi.demoSendOTP(email.toLowerCase().trim());
            setResendCooldown(RESEND_COOLDOWN);
            setStep(1);
        } catch (err) {
            const msg = err.response?.data?.error || err.message || 'Failed to send code. Please try again.';
            setEmailError(msg);
        } finally {
            setLoading(false);
        }
    };

    // ——————————————————————————————————————
    // Step 1: verify OTP
    // ——————————————————————————————————————
    const handleVerifyOTP = async () => {
        if (!otp.trim() || otp.trim().length !== 6) {
            setOtpError('Please enter the 6-digit code sent to your email.');
            return;
        }
        setOtpError('');
        setLoading(true);
        try {
            const response = await userApi.demoVerifyOTP(email, otp.trim());
            pendingAuth.current = {
                user: response.data.user,
                accessToken: response.data.accessToken,
            };
            setStep(2);
        } catch (err) {
            const msg = err.response?.data?.error || err.message || 'Invalid code. Please try again.';
            setOtpError(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = async () => {
        if (resendCooldown > 0) return;
        setOtpError('');
        setLoading(true);
        try {
            await userApi.demoSendOTP(email.toLowerCase().trim());
            setResendCooldown(RESEND_COOLDOWN);
            setOtp('');
        } catch (err) {
            const msg = err.response?.data?.error || err.message || 'Failed to resend code.';
            setOtpError(msg);
        } finally {
            setLoading(false);
        }
    };

    // ——————————————————————————————————————
    // Step 2: terms → just advance
    // ——————————————————————————————————————
    const handleAcceptTerms = () => {
        if (!agreedToTerms) return;
        setStep(3);
    };

    // ——————————————————————————————————————
    // Step 3: updates preference → finalize session
    // ——————————————————————————————————————
    const handleFinalize = async (subscribes) => {
        setLoading(true);
        try {
            await userApi.demoUpdateSubscription(email.toLowerCase().trim(), subscribes);
        } catch (err) {
            // Non-fatal — proceed regardless
            console.warn('Demo subscription update failed:', err);
        }
        try {
            const { user, accessToken } = pendingAuth.current;
            setAccessToken(accessToken);
            dispatch(demoLoginUser.success(user));
            navigate('/user-dashboard', { replace: true });
        } catch (err) {
            console.error('Demo finalize error:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                {/* Step indicator */}
                <div className={styles.stepIndicator}>
                    {STEP_META.map((s, i) => (
                        <div
                            key={i}
                            className={`${styles.stepDot} ${i === step ? styles.active : ''} ${i < step ? styles.done : ''}`}
                        >
                            <span className={styles.stepIcon}>{s.icon}</span>
                            <span className={styles.stepLabel}>{s.label}</span>
                        </div>
                    ))}
                </div>

                {/* ── Step 0: Email ───────────────────────────── */}
                {step === 0 && (
                    <div className={styles.stepContent}>
                        <div className={styles.badge}>Demo Mode</div>
                        <h2 className={styles.title}>Welcome to Prepal Demo</h2>
                        <p className={styles.description}>
                            Experience our AI-powered SAT preparation platform. Enter your email
                            to receive a verification code and start exploring.
                        </p>

                        <div className={styles.field}>
                            <label className={styles.label}>Email Address</label>
                            <Input
                                size="large"
                                placeholder="you@example.com"
                                value={email}
                                onChange={e => {
                                    setEmail(e.target.value);
                                    if (emailError) setEmailError('');
                                }}
                                onPressEnter={handleSendOTP}
                                status={emailError ? 'error' : ''}
                                disabled={loading}
                            />
                            {emailError && <p className={styles.errorMsg}>{emailError}</p>}
                        </div>

                        <button
                            className={styles.primaryBtn}
                            onClick={handleSendOTP}
                            disabled={loading || !email}
                        >
                            {loading ? <Spin size="small" /> : 'Send Verification Code'}
                        </button>
                    </div>
                )}

                {/* ── Step 1: OTP ─────────────────────────────── */}
                {step === 1 && (
                    <div className={styles.stepContent}>
                        <div className={styles.badge}>Check Your Inbox</div>
                        <h2 className={styles.title}>Enter Verification Code</h2>
                        <p className={styles.description}>
                            We sent a 6-digit code to <strong>{email}</strong>. It expires in 10 minutes.
                        </p>

                        <div className={styles.field}>
                            <Input
                                size="large"
                                placeholder="000000"
                                maxLength={6}
                                value={otp}
                                onChange={e => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    setOtp(val);
                                    if (otpError) setOtpError('');
                                }}
                                onPressEnter={handleVerifyOTP}
                                status={otpError ? 'error' : ''}
                                disabled={loading}
                                className={styles.otpInput}
                            />
                            {otpError && <p className={styles.errorMsg}>{otpError}</p>}
                        </div>

                        <button
                            className={styles.primaryBtn}
                            onClick={handleVerifyOTP}
                            disabled={loading || otp.length !== 6}
                        >
                            {loading ? <Spin size="small" /> : 'Verify Code'}
                        </button>

                        <div className={styles.resendRow}>
                            {resendCooldown > 0 ? (
                                <span className={styles.cooldown}>Resend code in {resendCooldown}s</span>
                            ) : (
                                <button
                                    className={styles.linkBtn}
                                    onClick={handleResendOTP}
                                    disabled={loading}
                                >
                                    Resend code
                                </button>
                            )}
                            <button
                                className={styles.linkBtn}
                                onClick={() => { setStep(0); setOtp(''); setOtpError(''); }}
                                disabled={loading}
                            >
                                Change email
                            </button>
                        </div>
                    </div>
                )}

                {/* ── Step 2: Terms ────────────────────────────── */}
                {step === 2 && (
                    <div className={styles.stepContent}>
                        <div className={styles.badge}>Almost There</div>
                        <h2 className={styles.title}>Terms &amp; Privacy Policy</h2>
                        <p className={styles.description}>
                            Before continuing, please read and agree to our terms.
                        </p>

                        <div className={styles.termsBox}>
                            <h4>Terms of Service Summary</h4>
                            <p>
                                By using Prepal, you agree to use the platform solely for
                                personal, non-commercial educational purposes. You must not
                                attempt to copy, reverse-engineer, or redistribute our content.
                                Your demo access is temporary and may be revoked at any time.
                            </p>
                            <h4>Privacy Policy Summary</h4>
                            <p>
                                We collect your email solely to manage this demo session.
                                We do not sell your data. Your session data is stored temporarily
                                and deleted automatically after 30 minutes of inactivity.
                                For full details, view our{' '}
                                <a href="https://www.studyprepal.com/terms-of-service" target="_blank" rel="noreferrer">Terms of Service</a>
                                {' '}and{' '}
                                <a href="https://www.studyprepal.com/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>.
                            </p>
                        </div>

                        <Checkbox
                            checked={agreedToTerms}
                            onChange={e => setAgreedToTerms(e.target.checked)}
                            className={styles.checkbox}
                        >
                            I have read and agree to the Terms of Service and Privacy Policy
                        </Checkbox>

                        <button
                            className={styles.primaryBtn}
                            onClick={handleAcceptTerms}
                            disabled={!agreedToTerms}
                        >
                            Continue
                        </button>
                    </div>
                )}

                {/* ── Step 3: Updates ──────────────────────────── */}
                {step === 3 && (
                    <div className={styles.stepContent}>
                        <div className={styles.updatesIcon}>🔔</div>
                        <h2 className={styles.title}>Stay in the Loop</h2>
                        <p className={styles.description}>
                            Would you like to receive product updates, SAT tips, and new
                            feature announcements from Prepal at <strong>{email}</strong>?
                        </p>

                        <button
                            className={styles.primaryBtn}
                            onClick={() => handleFinalize(true)}
                            disabled={loading}
                        >
                            {loading ? <Spin size="small" /> : 'Yes, keep me updated'}
                        </button>
                        <button
                            className={styles.secondaryBtn}
                            onClick={() => handleFinalize(false)}
                            disabled={loading}
                        >
                            No thanks, start demo
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
